import {
  Identifiable,
  InvitedMember,
  inviteMembersError,
  InviteMembersError,
  resource,
  Resource,
} from "@planzone/dto";
import { isEmailValid } from "../../entities/emailValidator";
import { findError } from "../../../entities/errorChecker";
import { Inviters } from "../../adapters/inviters";
import { present } from "../../../utils/presenter";
import { Interactor } from "./types";

function hasFailure(
  results: {
    invitation: { member: InvitedMember; error: InviteMembersError };
    isSuccess: boolean;
  }[],
): boolean {
  return !!results.find(({ isSuccess }) => !isSuccess);
}

function filterFailures(
  results: {
    invitation: { member: InvitedMember; error: InviteMembersError };
    isSuccess: boolean;
  }[],
): { member: InvitedMember; error: InviteMembersError }[] {
  return results
    .filter(({ isSuccess }) => !isSuccess)
    .map(({ invitation }) => invitation);
}

function filterSuccess(
  results: {
    invitation: { member: InvitedMember; resource: Identifiable<Resource> };
    isSuccess: boolean;
  }[],
): { member: InvitedMember; resource: Identifiable<Resource> }[] {
  return results
    .filter(({ isSuccess }) => isSuccess)
    .map(({ invitation }) => invitation);
}

function validateMember(member: InvitedMember): InviteMembersError {
  const isEmailInvalid = !isEmailValid(member.email);
  const isLastNameInvalid = !member.lastName;
  const isFirstNameInvalid = !member.firstName;
  const isRoleInvalid = member.role === null;

  return {
    ...inviteMembersError,
    emptyRole: isRoleInvalid,
    invalidEmail: isEmailInvalid,
    invalidLastName: isLastNameInvalid,
    invalidFirstName: isFirstNameInvalid,
  };
}

function createInteractor(inviters: Inviters): Interactor {
  return ({ planzone, members, message }) =>
    present(
      Promise.all(
        members.map((member) => {
          const error = validateMember(member);

          return !findError(error)
            ? inviters
                .sendInvitation(planzone, member, message)
                .then((resource) => ({
                  invitation: { member, resource, error: inviteMembersError },
                  isSuccess: true,
                }))
                .catch((error) => ({
                  invitation: {
                    member,
                    error,
                    resource: { ...resource, uuid: "" },
                  },
                  isSuccess: false,
                }))
            : Promise.resolve({
                invitation: {
                  member,
                  error,
                  resource: { ...resource, uuid: "" },
                },
                isSuccess: false,
              });
        }),
      ).then((results) =>
        !hasFailure(results)
          ? { planzone, message, members: filterSuccess(results) }
          : Promise.reject({
              planzone,
              message,
              failedInvitations: filterFailures(results),
              succeededInvitations: filterSuccess(results),
            }),
      ),
    );
}

export type { Interactor };

export { createInteractor };
